import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function FAQManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ question:'',answer:'',category:'',position:0,is_active:true });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const fetch = useCallback(() => { api.get('/faqs').then(r => setItems(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const resetForm = () => { setForm({ question:'',answer:'',category:'',position:0,is_active:true }); setEditing(null); setShowForm(false); };
  const handleEdit = (f) => { setEditing(f); setForm({ question:f.question||'',answer:f.answer||'',category:f.category||'',position:parseInt(f.position)||0,is_active:f.is_active===true||f.is_active==='true'||f.is_active==='True' }); setShowForm(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.question.trim() || !form.answer.trim()) { toast.error('Soru ve cevap gerekli'); return; }
    setIsSubmitting(true);
    try {
      if (editing) { await api.put(`/faqs/${editing.id}`, form); toast.success('Güncellendi'); }
      else { await api.post('/faqs', form); toast.success('Eklendi'); }
      resetForm(); fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata oluştu'); }
    finally { setIsSubmitting(false); }
  };

  const handleDelete = async (id) => { if (!window.confirm('Silmek istediğinize emin misiniz?')) return; try { await api.delete(`/faqs/${id}`); toast.success('Silindi'); fetch(); } catch { toast.error('Hata'); } };

  return (
    <div data-testid="admin-faq-manager">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-semibold">SSS Yönetimi</h1>
        <button onClick={() => { resetForm(); setShowForm(true); }} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium"><Plus className="w-4 h-4" /> Yeni Soru</button>
      </div>

      {showForm && (
        <div className="mb-8 p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <h3 className="font-heading text-lg font-semibold mb-4">{editing ? 'Düzenle' : 'Yeni SSS'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><label className="block text-sm font-medium mb-1.5">Soru *</label><input value={form.question} onChange={e => setForm(f=>({...f,question:e.target.value}))} className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]" /></div>
            <div><label className="block text-sm font-medium mb-1.5">Cevap *</label><textarea value={form.answer} onChange={e => setForm(f=>({...f,answer:e.target.value}))} rows={4} className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]" /></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium mb-1.5">Kategori</label><input value={form.category} onChange={e => setForm(f=>({...f,category:e.target.value}))} placeholder="Genel, Projeler, Ödeme..." className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm" /></div>
              <div><label className="block text-sm font-medium mb-1.5">Sıra</label><input type="number" value={form.position} onChange={e => setForm(f=>({...f,position:parseInt(e.target.value)||0}))} className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={e => setForm(f=>({...f,is_active:e.target.checked}))} className="rounded" /> Aktif</label>
            <div className="flex gap-3">
              <button type="submit" disabled={isSubmitting} className="px-6 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium disabled:opacity-50">{isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}</button>
              <button type="button" onClick={resetForm} className="px-6 py-2.5 rounded-lg border border-input text-sm hover:bg-secondary">İptal</button>
            </div>
          </form>
        </div>
      )}

      {loading ? <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>
      : items.length === 0 ? <div className="text-center py-10 text-muted-foreground">Henüz SSS yok</div>
      : <div className="space-y-2">{items.map(f => (
        <div key={f.id} className="rounded-xl bg-card border border-border overflow-hidden">
          <div className="flex items-center gap-3 p-4 cursor-pointer" onClick={() => setExpanded(expanded===f.id?null:f.id)}>
            <div className="flex-1"><div className="font-medium text-sm">{f.question}</div>{f.category && <span className="text-xs text-muted-foreground">{f.category}</span>}</div>
            {expanded===f.id ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            <div className="flex gap-1">
              <button onClick={(e) => { e.stopPropagation(); handleEdit(f); }} className="p-1.5 rounded-lg hover:bg-secondary"><Pencil className="w-3.5 h-3.5" /></button>
              <button onClick={(e) => { e.stopPropagation(); handleDelete(f.id); }} className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          {expanded===f.id && <div className="px-4 pb-4 text-sm text-muted-foreground border-t border-border pt-3">{f.answer}</div>}
        </div>
      ))}</div>}
    </div>
  );
}
